import { adminApi, type AdminAssetPurpose, type AdminPresignedAssetUpload } from "./adminApi"

const READY_POLL_INTERVAL_MS = 1500
const READY_POLL_TIMEOUT_MS = 60_000

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

// POST the bytes to the presigned S3 policy. The policy fields (content-type +
// exact content-length) are enforced by S3 itself; the `file` field MUST be
// appended last or S3 rejects the form. In dev the browser can't POST
// cross-origin to the bucket (its CORS isn't managed locally), so forward
// through the same-origin dev proxy; production posts straight to S3, where the
// bucket CORS allows the app origin.
const uploadToPresignedPost = async (
  upload: AdminPresignedAssetUpload,
  file: Blob
): Promise<void> => {
  const form = new FormData()
  for (const [key, value] of Object.entries(upload.fields)) form.append(key, value)
  form.append("file", file)

  const target = import.meta.env.DEV
    ? `/__s3-upload?url=${encodeURIComponent(upload.url)}&key=${encodeURIComponent(upload.key)}`
    : upload.url

  const response = await fetch(target, { body: form, method: "POST" })
  if (!response.ok) throw new Error(`asset_upload_failed_${response.status}`)
}

/**
 * Upload a file through the scoped platform asset pipeline and return its durable
 * id + public CDN url. Replaces the retired `/cdn-token` direct-write flow:
 *   1. create a pending asset + presigned S3 POST (POST /admin/uploads)
 *   2. upload the bytes straight to S3
 *   3. poll GET /admin/assets/{id} until the asset-worker finalizes it
 *      (status "ready" + publicUrl) or it fails / times out
 * The caller uses `publicUrl` as a cover/effect url, or `assetId` where the
 * backend links the asset directly (e.g. UI-customization logos).
 */
export const uploadPlatformAsset = async (params: {
  contentType?: string
  file: Blob
  fileName?: string
  merchantId?: string
  purpose: AdminAssetPurpose
  suppressPaywall?: boolean
}): Promise<{ assetId: string; publicUrl: string }> => {
  const contentType = params.contentType ?? params.file.type
  if (!contentType) throw new Error("asset_upload_missing_content_type")

  const { asset, upload } = await adminApi.createAssetUpload(
    {
      contentType,
      ...(params.fileName ? { fileName: params.fileName } : {}),
      ...(params.merchantId ? { merchantId: params.merchantId } : {}),
      purpose: params.purpose,
      sizeBytes: params.file.size,
    },
    { suppressPaywall: params.suppressPaywall }
  )

  await uploadToPresignedPost(upload, params.file)

  const deadline = Date.now() + READY_POLL_TIMEOUT_MS
  while (Date.now() < deadline) {
    const current = await adminApi.getAsset(asset.assetId, {
      merchantId: params.merchantId,
      suppressPaywall: params.suppressPaywall,
    })
    if (current.status === "ready" && current.publicUrl)
      return { assetId: current.assetId, publicUrl: current.publicUrl }
    if (current.status === "failed") throw new Error(current.failureReason || "asset_upload_failed")
    await delay(READY_POLL_INTERVAL_MS)
  }

  throw new Error("asset_upload_timeout")
}
